import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export interface DocumentComment {
  id: string;
  documentId: string;
  createdByMemberId: string | null;
  createdByAgentId: string | null;
  createdByType: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind: string | null;
  createdByName: string | null;
  /** 작성에 사용된 도구(에이전트 설정) 이름. 행위 주체 축과 직교한다. */
  createdByAgentName: string | null;
  updatedByMemberId?: string | null;
  updatedByAgentId?: string | null;
  updatedByCredentialKind?: string | null;
  updatedByName?: string | null;
  updatedByAgentName?: string | null;
  // 본문 인용은 별도 필드 없이 content 안에 blockquote로 포함된다.
  content: string;
  // 답글은 이 목록에 실리지 않는다(별도 replies 엔드포인트). 존재 여부는 이 값으로만 알 수 있다.
  replyCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

type DocumentCommentListResponse = {
  data: DocumentComment[];
  meta: PaginationMeta;
};

type DocumentCommentResponse = {
  data: DocumentComment;
};

export type DocumentCommentQueryParams = {
  order?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
};

const buildUrl = (projectId: string, documentId: string, suffix = '', params?: DocumentCommentQueryParams) => {
  const queryParams = new URLSearchParams();
  if (params?.order) queryParams.set('order', params.order);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));
  const query = queryParams.toString();
  return `${getApiUrl()}/api/projects/${projectId}/documents/${documentId}/comments${suffix}${query ? `?${query}` : ''}`;
};

export const fetchDocumentComments = async (
  token: string,
  projectId: string,
  documentId: string,
  params: DocumentCommentQueryParams = {},
): Promise<PagedData<DocumentComment>> => {
  const response = await fetchWithAuthRetry(buildUrl(projectId, documentId, '', params), {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch document comments: ${response.status}`);
  }

  return (await response.json()) as DocumentCommentListResponse;
};

export const createDocumentComment = async (
  token: string,
  projectId: string,
  documentId: string,
  input: { content: string },
): Promise<DocumentComment> => {
  const response = await fetchWithAuthRetry(buildUrl(projectId, documentId), {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify({ content: input.content }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create document comment: ${response.status}`);
  }

  return ((await response.json()) as DocumentCommentResponse).data;
};

export const updateDocumentComment = async (
  token: string,
  projectId: string,
  documentId: string,
  commentId: string,
  input: { content: string },
): Promise<DocumentComment> => {
  const response = await fetchWithAuthRetry(buildUrl(projectId, documentId, `/${commentId}`), {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update document comment: ${response.status}`);
  }

  return ((await response.json()) as DocumentCommentResponse).data;
};

export const deleteDocumentComment = async (
  token: string,
  projectId: string,
  documentId: string,
  commentId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(buildUrl(projectId, documentId, `/${commentId}`), {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete document comment: ${response.status}`);
  }
};
